// src/screens/CreatorMetricsScreen.js
// ============================================================================
// CREATOR METRICS (founder 2026-07-10): the full self-serve dashboard behind
// the Wallet's "Your Creator Metrics" card. Shows, LIVE: time streamed, events
// entered/hosted, games played, stream audience (plain stats, never money
// drivers: copy doctrine v3), the support received (Super Crits, legacy event
// boosts; Crit rumbles and chat gifts while Crits are on) and, in its own
// card (evergreen 3.7), the creator's earnings: a pending / payable strip,
// the Super Crit, Rumble, Memberships and Team memberships (evergreen 3.5b)
// lanes and the month's monthly payment. Lane derivation lives in
// utils/earningsLanes.js (pure, tested).
// Data: getMyCreatorMetrics (works in comp test mode too; test-mode Super Crit
// money is shown but flagged; it never funds real comp).
// Audience (AUDIENCE_METRICS_DESIGN.md, AM-2): getMyAudienceMetrics feeds the
// Audience card, the "Who watched live" block and the Videos card. Counts
// only, never names; the server hides small splits. Its strings come from
// utils/audienceView.js (pure, tested); a failure hides only those rows.
// ============================================================================

import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Ionicons } from '@expo/vector-icons';
import { auth } from '../../firebaseConfig';
import Header from '../components/Header';
import { GAME_PROFILES } from '../constants/gameData';
import useCritsEnabled from '../hooks/useCritsEnabled';
import useMembershipsLive from '../hooks/useMembershipsLive';
import { deriveEarningsLanes } from '../utils/earningsLanes';
import {
  deriveAudienceView,
  streamSignedOutHint,
  LIVE_VIEWERS_LABEL,
  LIVE_WATCH_LABEL,
} from '../utils/audienceView';

const COLORS = {
  BG: '#0b0b0b',
  CARD: '#111',
  CARD_BORDER: '#222',
  GOLD: '#FCD000',
  TEXT: '#EDEDED',
  DIM: '#888',
  CYAN: '#3DD6F5',
};

const monthKeyOf = (d) => `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;

// Current month first, then the 5 before it (metrics exist from launch month on).
const recentMonths = () => {
  const now = new Date();
  return Array.from({ length: 6 }, (_, i) => {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    // Show the year on chips that cross into the previous year: in January
    // a bare "Dec" is ambiguous.
    const priorYear = d.getUTCFullYear() !== now.getUTCFullYear();
    return {
      key: monthKeyOf(d),
      label: d.toLocaleString('en-US', { month: 'short', year: priorYear ? '2-digit' : undefined, timeZone: 'UTC' }),
    };
  });
};

const fmtMinutes = (mins) => {
  const m = Math.round(Number(mins) || 0);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
};

const fmtDollars = (centsAmt) => {
  const d = (Number(centsAmt) || 0) / 100;
  return `$${d % 1 ? d.toFixed(2) : d.toFixed(0)}`;
};

const fmtNum = (n) => {
  const v = Math.round((Number(n) || 0) * 10) / 10;
  return Number.isInteger(v) ? v.toLocaleString('en-US') : v.toFixed(1);
};

// "Oct 1, 2026": the day the monthly payment was credited.
const fmtDate = (ms) => new Date(Number(ms) || 0).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const gameName = (key) => {
  if (!key || key === 'other') return 'Other';
  const p = GAME_PROFILES?.[key];
  return (p && p.displayName) || key.toUpperCase();
};

export default function CreatorMetricsScreen({ navigation }) {
  const functions = getFunctions();
  const months = recentMonths();
  const [monthKey, setMonthKey] = useState(months[0].key);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const critsEnabled = useCritsEnabled(); // settings/economy.critsEnabled
  // Channel memberships (3.5a): the footnote names them once they have ever
  // launched (a paused launch never drops them from the list). Team
  // memberships (3.5b) have their own flag: the Team memberships lane and the
  // footnote's team sentence key on it ALONE, so a team-only launch shows them.
  const { everLaunched: membershipsEverLaunched, teamEverLaunched: teamMembershipsEverLaunched } = useMembershipsLive();

  const load = useCallback(async (mk) => {
    setLoading(true);
    setError(null);
    try {
      const fn = httpsCallable(functions, 'getMyCreatorMetrics');
      const result = await fn({ monthKey: mk });
      setData(result.data || null);
    } catch (err) {
      console.warn('[CreatorMetrics] load failed:', err.message);
      setError('Could not load your metrics — try again in a moment.');
    } finally {
      setLoading(false);
    }
  }, [functions]);

  // Audience (AM-2): its own read-only callable, run in parallel with the one
  // above. A failure leaves aud null, so only the new rows stay hidden. The
  // request counter drops a response that lands after a newer month chip.
  const [aud, setAud] = useState(null);
  const audReq = useRef(0);
  const loadAudience = useCallback(async (mk) => {
    audReq.current += 1;
    const req = audReq.current;
    setAud(null);
    try {
      const fn = httpsCallable(functions, 'getMyAudienceMetrics');
      const result = await fn({ monthKey: mk });
      if (req === audReq.current) setAud(result.data || null);
    } catch (err) {
      console.warn('[CreatorMetrics] audience load failed:', err.message);
      if (req === audReq.current) setAud(null);
    }
  }, [functions]);

  useEffect(() => {
    if (!auth.currentUser) {
      navigation.navigate('Login');
      return;
    }
    load(monthKey);
    loadAudience(monthKey);
  }, [monthKey, load, loadAudience, navigation]);

  const streaming = data?.streaming || {};
  const events = data?.events || {};
  const support = data?.support || {};
  const audience = data?.audience || {};

  const gamesList = Object.entries(events.games || {}).sort((a, b) => b[1] - a[1]);
  const totalStreamViewers = (streaming.viewers || 0) + (streaming.anonViewers || 0);

  // Audience view (AM-2): null until getMyAudienceMetrics answers for this
  // month (or after it fails), and then none of its rows render. When it has
  // the live totals, the Streaming cells use them, so the "Who watched live"
  // split always adds up to the numbers above it.
  const av = deriveAudienceView(aud, { membershipsEverLaunched, monthKey, nowMs: Date.now() });
  const liveViewersText = av && av.live.totalText !== null ? av.live.totalText : fmtNum(totalStreamViewers);
  const liveWatchText = av && av.live.minutesText !== null
    ? av.live.minutesText
    : fmtMinutes((streaming.watchMinutes || 0) + (streaming.anonWatchMinutes || 0));
  const liveBlock = av ? av.live.block : null;
  const signedOutHint = streamSignedOutHint(streaming.anonViewers);

  // Earnings lanes (evergreen 3.7): one pure derivation feeds the support
  // card's Super Crit cell and the whole "Your Earnings" card. It keeps every
  // pre-3.7 visibility rule (run-lane hint, direct block, Rumbles,
  // Memberships) and falls back per lane on an older server (no pending /
  // available split, no monthly payment), so nothing renders that the server
  // did not send. Every cents field it returns is a number, so the
  // formatters below never see undefined.
  const lanes = deriveEarningsLanes(data, { critsEnabled, membershipsEverLaunched, teamMembershipsEverLaunched, monthKey });
  const superCritTotalCents = lanes.superCrits.grossCents;
  const superCritTotalCount = lanes.superCrits.count;
  const superCritPctLabel = lanes.superCrits.pctLabel;
  const showSuperCritRunFootnote = lanes.superCrits.showRunFootnote;
  const monthLabel = lanes.pool.monthLabel;
  // The monthly payment's itemized shares, in the lane names the credit
  // notification uses (compCreditCopy on the server).
  const poolIncludes = (() => {
    const sc = lanes.pool.superCritRunCents;
    const fl = lanes.pool.floodsCents;
    if (sc > 0 && fl > 0) return `Includes ${fmtDollars(sc)} of Super Crit shares and ${fmtDollars(fl)} of Rumble shares.`;
    if (sc > 0) return `Includes ${fmtDollars(sc)} of Super Crit shares.`;
    if (fl > 0) return `Includes ${fmtDollars(fl)} of Rumble shares.`;
    return '';
  })();

  const statCell = (value, label, accent) => (
    <View style={styles.statCell} key={label}>
      <Text style={[styles.statValue, accent && { color: accent }]}>{value}</Text>
      <Text style={styles.statLabel}>{label}</Text>
    </View>
  );

  return (
    <View style={styles.container}>
      <Header navigation={navigation} />
      <ScrollView style={{ flex: 1 }} contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
        <View style={styles.titleRow}>
          <View style={{ flex: 1 }}>
            <Text style={styles.title}>Creator Metrics</Text>
            <Text style={styles.subtitle}>
              Your streams, events, audience and support, updated live.
            </Text>
          </View>
        </View>

        {/* Month picker */}
        <View style={styles.monthRow}>
          {months.map((m) => (
            <TouchableOpacity
              key={m.key}
              style={[styles.monthChip, monthKey === m.key && styles.monthChipActive]}
              onPress={() => setMonthKey(m.key)}
            >
              <Text style={[styles.monthChipText, monthKey === m.key && styles.monthChipTextActive]}>
                {m.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        {loading ? (
          <ActivityIndicator color={COLORS.GOLD} style={{ marginVertical: 60 }} />
        ) : error ? (
          <Text style={styles.errorText}>{error}</Text>
        ) : !data ? null : (
          <>
            {/* Receiving a share of Super Crits and Rumbles requires identity +
                18+ verification: until then nothing is credited, so say so
                instead of implying a payout. */}
            {data.earningVerified === false ? (
              <TouchableOpacity style={styles.memberBanner} onPress={() => navigation.navigate('IdVerification')}>
                <Ionicons name="id-card-outline" size={15} color={COLORS.GOLD} style={{ marginRight: 8 }} />
                <Text style={styles.memberBannerText}>
                  <Text style={styles.memberBannerStrong}>Verify to earn.</Text> Identity and age (18+)
                  verification is the only requirement to receive your share of Super Crits and
                  Rumbles. Nothing is credited before you verify.
                </Text>
                <Ionicons name="chevron-forward" size={16} color={COLORS.GOLD} />
              </TouchableOpacity>
            ) : null}

            {/* Audience (AM-2): followers and members right now, new
                followers this month. Member rows only once memberships have
                ever launched or the creator has a member (dark otherwise). */}
            {av && av.audience ? (
              <View style={styles.card}>
                <Text style={styles.cardTitle}>{av.audience.title}</Text>
                <View style={styles.statRow}>
                  {av.audience.cells.map((c) => statCell(c.value, c.label))}
                </View>
                {av.audience.tierLine ? (
                  <Text style={styles.audLine}>{av.audience.tierLine}</Text>
                ) : null}
                <Text style={styles.footnote}>{av.audience.footnote}</Text>
              </View>
            ) : null}

            {/* Streaming */}
            <View style={styles.card}>
              <View style={styles.cardTitleRow}>
                <Text style={styles.cardTitle}>Streaming</Text>
                {streaming.liveNow ? (
                  <View style={styles.liveNowChip}>
                    <View style={styles.liveNowDot} />
                    <Text style={styles.liveNowText}>LIVE NOW</Text>
                  </View>
                ) : null}
              </View>
              <View style={styles.statRow}>
                {statCell(fmtMinutes(streaming.minutes), 'time streamed')}
                {statCell(fmtNum(streaming.sessions), `session${streaming.sessions === 1 ? '' : 's'}`)}
                {statCell(liveViewersText, LIVE_VIEWERS_LABEL)}
                {statCell(liveWatchText, LIVE_WATCH_LABEL)}
              </View>
              {(streaming.minutes || 0) > 0 ? null : (
                <Text style={styles.footnote}>
                  Go live with CritCast for Windows or your OBS stream key and this fills in.
                </Text>
              )}
              {/* Who watched live (AM-2): the live audience split by follower
                  status, plus members who watched once member rows show. The
                  server sends null for any split under its floor. */}
              {liveBlock ? (
                <>
                  <Text style={styles.laneTitle}>{liveBlock.title}</Text>
                  <Text style={styles.audLine}>{liveBlock.viewersLine}</Text>
                  {liveBlock.watchLine ? <Text style={styles.audLine}>{liveBlock.watchLine}</Text> : null}
                  {liveBlock.memberLine ? <Text style={styles.audLine}>{liveBlock.memberLine}</Text> : null}
                  {liveBlock.memberWatchLine ? <Text style={styles.audLine}>{liveBlock.memberWatchLine}</Text> : null}
                  {liveBlock.notes.map((n) => (
                    <Text key={n} style={styles.audNote}>{n}</Text>
                  ))}
                  {liveBlock.footnote ? <Text style={styles.footnote}>{liveBlock.footnote}</Text> : null}
                  {liveBlock.capNote ? <Text style={styles.audNote}>{liveBlock.capNote}</Text> : null}
                </>
              ) : null}
              {signedOutHint ? (
                <View style={styles.hintBox}>
                  <Ionicons name="eye-outline" size={15} color={COLORS.GOLD} style={{ marginRight: 8 }} />
                  <Text style={styles.hintText}>
                    <Text style={styles.hintStrong}>{signedOutHint.strong}</Text>
                    {signedOutHint.rest}
                  </Text>
                </View>
              ) : null}
            </View>

            {/* Videos (AM-2): the month's audience for stream videos and
                uploads, counts only (no minutes). */}
            {av && av.videos ? (
              <View style={styles.card}>
                <Text style={styles.cardTitle}>{av.videos.title}</Text>
                {av.videos.countedFrom ? (
                  <Text style={styles.footnote}>{av.videos.countedFrom}</Text>
                ) : null}
                {av.videos.rows.map((r) => (
                  <View key={r.key}>
                    <Text style={styles.laneTitle}>{r.title}</Text>
                    <Text style={styles.audLine}>{r.line}</Text>
                    {r.memberLine ? <Text style={styles.audLine}>{r.memberLine}</Text> : null}
                  </View>
                ))}
                {av.videos.notes.map((n) => (
                  <Text key={n} style={styles.audNote}>{n}</Text>
                ))}
                {av.videos.footnote ? <Text style={styles.footnote}>{av.videos.footnote}</Text> : null}
                {av.videos.capNote ? <Text style={styles.audNote}>{av.videos.capNote}</Text> : null}
              </View>
            ) : null}

            {/* Events */}
            <View style={styles.card}>
              <Text style={styles.cardTitle}>Events</Text>
              <View style={styles.statRow}>
                {statCell(fmtNum(events.entered), 'entered')}
                {statCell(fmtNum(events.hosted), 'hosted')}
                {statCell(events.bestRank ? `#${events.bestRank}` : '—', 'best finish')}
                {critsEnabled && statCell(fmtNum(events.earnedCrits), 'Crits won')}
              </View>
              {gamesList.length > 0 ? (
                <View style={styles.gamesRow}>
                  {gamesList.map(([g, count]) => (
                    <View key={g} style={styles.gameChip}>
                      <Text style={styles.gameChipText}>{gameName(g)}</Text>
                      <Text style={styles.gameChipCount}>×{count}</Text>
                    </View>
                  ))}
                </View>
              ) : (
                <Text style={styles.footnote}>
                  No events this month yet.
                </Text>
              )}
              {(audience.viewers || 0) + (audience.anonViewers || 0) > 0 ? (
                <Text style={styles.footnote}>
                  Match audience: {fmtNum(audience.viewers)} signed-in + {fmtNum(audience.anonViewers)} anonymous viewers,{' '}
                  {fmtMinutes((audience.watchMinutes || 0) + (audience.anonWatchMinutes || 0))} watched.
                </Text>
              ) : null}
            </View>

            {/* Support received */}
            <View style={styles.card}>
              <Text style={styles.cardTitle}>Support From Your Audience</Text>
              {/* Crit-era cells (Crit rumbles, chat gifts, and Crits won above)
                  render only while Crits are on. */}
              <View style={styles.statRow}>
                {critsEnabled && statCell(fmtNum(support.rumbleCrits), `Crits from ${fmtNum(support.rumbleCount)} rumble${support.rumbleCount === 1 ? '' : 's'}`, COLORS.GOLD)}
                {statCell(fmtDollars(superCritTotalCents), `Super Crit${superCritTotalCount === 1 ? '' : 's'} (${fmtNum(superCritTotalCount)})`, COLORS.GOLD)}
                {/* Legacy event-Rumble checkouts (pre-3.3 floods) only; 3.3
                    event Rumbles are in the Rumbles lane of the Your Earnings
                    card. */}
                {(Number(support.floodCents) || 0) > 0 && statCell(fmtDollars(support.floodCents), 'event boosts', COLORS.GOLD)}
                {critsEnabled && statCell(fmtNum(support.giftCrits), `Crits gifted in your chat (${fmtNum(support.giftCount)})`)}
              </View>
            </View>

            {/* Your Earnings (evergreen 3.7): the creator's share of the
                month's support, per lane, with pending and payable. Pending =
                still settling; available / payable = matured into the
                balance, which the daily sweep sends to the payout account
                once it is set up. The monthly payment lane shows only a month
                the run credited pool money; a run row that carried only a
                Super Crit share feeds the Super Crit hint instead. */}
            <View style={styles.card}>
              <Text style={styles.cardTitle}>Your Earnings</Text>
              <Text style={styles.subtitle}>Your share of the support you received in {monthLabel}.</Text>
              {/* Summary strip: hidden when any lane with money lacks the
                  pending / available split (older server), so it never shows
                  a total that misses a lane. */}
              {lanes.summary.hasSplit ? (
                <View style={styles.statRow}>
                  {statCell(fmtDollars(lanes.summary.pendingCents), 'pending', COLORS.GOLD)}
                  {statCell(fmtDollars(lanes.summary.availableCents), 'payable', COLORS.GOLD)}
                </View>
              ) : null}

              {/* Super Crits: the run-lane hint (the estimate until the
                  month's monthly payment has paid the share, then the paid
                  figure) and the direct-lane block. */}
              {lanes.superCrits.showRunHint || lanes.superCrits.showDirect ? (
                <>
                  <Text style={styles.laneTitle}>Super Crits</Text>
                  {lanes.superCrits.showRunHint ? (
                    <View style={[styles.hintBox, styles.laneHint]}>
                      <Ionicons name="cash-outline" size={15} color={COLORS.GOLD} style={{ marginRight: 8 }} />
                      <Text style={styles.hintText}>
                        {lanes.superCrits.hintMode === 'paid' ? (
                          <Text style={styles.hintStrong}>
                            Your Super Crit shares paid with the {monthLabel} monthly payment: {fmtDollars(lanes.superCrits.hintAmountCents)}.
                          </Text>
                        ) : (
                          <>
                            <Text style={styles.hintStrong}>
                              Your Super Crit share: {fmtDollars(lanes.superCrits.hintAmountCents)}.
                            </Text>
                            {` CritCash credits you a share of each Super Crit, currently up to ${superCritPctLabel}% after payment-processing costs (lower on small purchases), itemized and paid in a later monthly payment.`}
                            {lanes.superCrits.runTestCents > 0
                              ? ` (Plus ${fmtDollars(lanes.superCrits.runTestCents)} from test payments, shown so you can watch it work; test money never pays out.)`
                              : ''}
                          </>
                        )}
                      </Text>
                    </View>
                  ) : null}
                  {/* Direct lane: this month's live direct Super Crit earnings from
                      the earnings ledger (pending = still settling, available =
                      payable). Never includes refused credits. */}
                  {lanes.superCrits.showDirect ? (
                    <View style={[styles.hintBox, styles.laneHint]}>
                      <Ionicons name="wallet-outline" size={15} color={COLORS.GOLD} style={{ marginRight: 8 }} />
                      <Text style={styles.hintText}>
                        Your share of each Super Crit you receive is credited to your earnings as pending until it's payable, no sooner than 48 hours after the purchase, and not before CritCash has received the funds.
                        {'\n'}
                        <Text style={styles.hintStrong}>
                          Pending: {fmtDollars(lanes.superCrits.pendingCents)} · Available: {fmtDollars(lanes.superCrits.availableCents)}
                        </Text>
                      </Text>
                    </View>
                  ) : null}
                </>
              ) : null}

              {/* Rumbles: this month's live credits from the earnings ledger.
                  The split line needs a 3.7 server; an older one gets the
                  credited total. */}
              {lanes.rumbles.show ? (
                <>
                  <Text style={styles.laneTitle}>Rumbles</Text>
                  <View style={[styles.hintBox, styles.laneHint]}>
                    <Ionicons name="flash-outline" size={15} color={COLORS.GOLD} style={{ marginRight: 8 }} />
                    <Text style={styles.hintText}>
                      Your share of each Rumble you receive is credited to your earnings as pending until it's payable, no sooner than 48 hours after the purchase, and not before CritCash has received the funds. An event Rumble is credited once the event's results are final.
                      {'\n'}
                      <Text style={styles.hintStrong}>
                        {lanes.rumbles.split
                          ? `Pending: ${fmtDollars(lanes.rumbles.pendingCents)} · Available: ${fmtDollars(lanes.rumbles.availableCents)}`
                          : `Rumbles credited: ${fmtDollars(lanes.rumbles.creditedCents)} (${fmtNum(lanes.rumbles.count)})`}
                      </Text>
                    </Text>
                  </View>
                </>
              ) : null}

              {/* Memberships: this month's live credits + current members,
                  plus the split line on a 3.7 server. */}
              {lanes.memberships.show ? (
                <>
                  <Text style={styles.laneTitle}>Memberships</Text>
                  <View style={[styles.hintBox, styles.laneHint]}>
                    <Ionicons name="star-outline" size={15} color={COLORS.GOLD} style={{ marginRight: 8 }} />
                    <View style={{ flex: 1 }}>
                      <Text style={styles.hintText}>
                        <Text style={styles.hintStrong}>
                          Memberships: {fmtNum(lanes.memberships.count)} payment{lanes.memberships.count === 1 ? '' : 's'} this month, {fmtDollars(lanes.memberships.creditedCents)} credited, {fmtNum(lanes.memberships.activeMembers)} active member{lanes.memberships.activeMembers === 1 ? '' : 's'}
                        </Text>
                        {lanes.memberships.split ? (
                          <Text style={styles.hintStrong}>
                            {'\n'}Pending: {fmtDollars(lanes.memberships.pendingCents)} · Available: {fmtDollars(lanes.memberships.availableCents)}
                          </Text>
                        ) : null}
                      </Text>
                      <TouchableOpacity
                        style={styles.inlineLink}
                        onPress={() => navigation.navigate('CreatorMemberships')}
                        accessibilityRole="link"
                      >
                        <Text style={styles.inlineLinkText}>Channel memberships</Text>
                        <Ionicons name="chevron-forward" size={14} color={COLORS.GOLD} />
                      </TouchableOpacity>
                    </View>
                  </View>
                </>
              ) : null}

              {/* Team memberships (evergreen 3.5b): this month's live credits
                  of team membership shares, with the split line. A share is
                  credited after the month it was paid in closes, so this
                  lane shows the previous month's team memberships. The link
                  opens the team settings screen, which resolves the
                  creator's team itself (no team, team gone, not captain). */}
              {lanes.teamMemberships.show ? (
                <>
                  <Text style={styles.laneTitle}>Team memberships</Text>
                  <View style={[styles.hintBox, styles.laneHint]}>
                    <Ionicons name="people-outline" size={15} color={COLORS.GOLD} style={{ marginRight: 8 }} />
                    <View style={{ flex: 1 }}>
                      <Text style={styles.hintText}>
                        <Text style={styles.hintStrong}>
                          Team memberships: {fmtNum(lanes.teamMemberships.count)} share{lanes.teamMemberships.count === 1 ? '' : 's'} credited this month, {fmtDollars(lanes.teamMemberships.creditedCents)}
                        </Text>
                        {lanes.teamMemberships.split ? (
                          <Text style={styles.hintStrong}>
                            {'\n'}Pending: {fmtDollars(lanes.teamMemberships.pendingCents)} · Available: {fmtDollars(lanes.teamMemberships.availableCents)}
                          </Text>
                        ) : null}
                        {'\n'}A share of your team's memberships is credited after the month it was paid in closes.
                      </Text>
                      <TouchableOpacity
                        style={styles.inlineLink}
                        onPress={() => navigation.navigate('CreatorTeamMemberships')}
                        accessibilityRole="link"
                        accessibilityLabel="Team memberships"
                      >
                        <Text style={styles.inlineLinkText}>Team memberships</Text>
                        <Ionicons name="chevron-forward" size={14} color={COLORS.GOLD} />
                      </TouchableOpacity>
                    </View>
                  </View>
                </>
              ) : null}

              {/* Monthly payment: the month's comp credit, shown only when it
                  carried pool money. Credited straight to the balance. */}
              {lanes.pool.mode === 'paid' ? (
                <>
                  <Text style={styles.laneTitle}>Monthly payment</Text>
                  <View style={[styles.hintBox, styles.laneHint]}>
                    <Ionicons name="calendar-outline" size={15} color={COLORS.GOLD} style={{ marginRight: 8 }} />
                    <Text style={styles.hintText}>
                      <Text style={styles.hintStrong}>
                        Monthly payment for {monthLabel}: {fmtDollars(lanes.pool.paidCents)}{lanes.pool.paidAtMs ? `, credited ${fmtDate(lanes.pool.paidAtMs)}` : ''}.
                      </Text>
                      {' It went to your balance and will be sent to your payout account automatically.'}
                      {poolIncludes ? ` ${poolIncludes}` : ''}
                    </Text>
                  </View>
                </>
              ) : null}

              <Text style={styles.footnote}>
                {membershipsEverLaunched
                  ? `CritCash credits you a share of each Super Crit, Rumble and membership payment you receive, currently up to ${superCritPctLabel}% after processing costs.`
                  : `CritCash credits you a share of each Super Crit and Rumble you receive, currently up to ${superCritPctLabel}% after payment-processing costs (lower on small purchases).`}{showSuperCritRunFootnote ? ' Super Crit shares not credited at purchase are itemized and paid in a later monthly payment.' : ''} A squad Super Crit or Rumble is shared equally among the verified squad members streaming when it's sent; an event Rumble is shared equally among the verified players who played, whatever their placement, once results are final.{teamMembershipsEverLaunched ? " A team membership is shared equally among the team's verified members who streamed linked to the team for at least 30 minutes in the month the payment was made, once that month closes." : ''} A pending share becomes available no sooner than 48 hours after the purchase and not before CritCash has received the funds; an available share has been added to your balance and will be sent to your payout account automatically.
              </Text>
            </View>

            <TouchableOpacity style={styles.backButton} onPress={() => navigation.navigate('Wallet')}>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <Ionicons name="arrow-back" size={18} color="#888" style={{ marginRight: 4 }} />
                <Text style={styles.backButtonText}>Back to Wallet</Text>
              </View>
            </TouchableOpacity>
          </>
        )}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: COLORS.BG },
  content: { padding: 16, paddingBottom: 100, maxWidth: 860, width: '100%', alignSelf: 'center' },
  titleRow: { flexDirection: 'row', alignItems: 'flex-start', marginTop: 8 },
  title: { color: '#fff', fontSize: 22, fontWeight: '800' },
  subtitle: { color: COLORS.DIM, fontSize: 13, marginTop: 4, lineHeight: 19 },
  errorText: { color: '#e05555', fontSize: 13, textAlign: 'center', marginTop: 40 },

  monthRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 16 },
  monthChip: {
    backgroundColor: '#161616', borderColor: '#2a2a2a', borderWidth: 1,
    borderRadius: 8, paddingVertical: 6, paddingHorizontal: 14,
  },
  monthChipActive: { backgroundColor: '#241d00', borderColor: COLORS.GOLD },
  monthChipText: { color: '#999', fontSize: 12, fontWeight: '700' },
  monthChipTextActive: { color: COLORS.GOLD },

  memberBanner: {
    flexDirection: 'row', alignItems: 'center', backgroundColor: '#1a1604',
    borderColor: '#4a3d00', borderWidth: 1, borderRadius: 8,
    paddingVertical: 10, paddingHorizontal: 12, marginTop: 12,
  },
  memberBannerText: { color: '#ccc', fontSize: 12, flex: 1, lineHeight: 17 },
  memberBannerStrong: { color: COLORS.GOLD, fontWeight: '700' },

  card: {
    backgroundColor: COLORS.CARD, borderColor: COLORS.CARD_BORDER, borderWidth: 1,
    borderRadius: 12, padding: 16, marginTop: 16,
  },
  cardTitleRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  cardTitle: { color: '#fff', fontSize: 15, fontWeight: '800' },

  statRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 14 },
  statCell: {
    flexGrow: 1, flexBasis: '22%', minWidth: 140, alignItems: 'center',
    backgroundColor: '#161616', borderRadius: 8, paddingVertical: 12, paddingHorizontal: 8,
  },
  statValue: { color: '#fff', fontSize: 17, fontWeight: '800' },
  statLabel: { color: COLORS.DIM, fontSize: 11, marginTop: 3, textAlign: 'center' },

  liveNowChip: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#2a0d0d', borderRadius: 5, paddingVertical: 3, paddingHorizontal: 8 },
  liveNowDot: { width: 6, height: 6, borderRadius: 3, backgroundColor: '#e02222', marginRight: 5 },
  liveNowText: { color: '#ff8a8a', fontSize: 10, fontWeight: '800', letterSpacing: 0.5 },

  footnote: { color: '#777', fontSize: 11.5, lineHeight: 17, marginTop: 12 },
  hintBox: {
    flexDirection: 'row', alignItems: 'flex-start', backgroundColor: '#1a1604',
    borderRadius: 8, padding: 10, marginTop: 12,
  },
  hintText: { color: '#ccc', fontSize: 12, lineHeight: 17, flex: 1 },
  hintStrong: { color: COLORS.GOLD, fontWeight: '700' },
  inlineLink: { flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-start', marginTop: 6 },
  inlineLinkText: { color: COLORS.GOLD, fontSize: 12, fontWeight: '700', marginRight: 2 },
  // Earnings card lanes: a small label above each lane's boxes, which sit
  // closer to it than a free-standing hint box does.
  laneTitle: { color: COLORS.DIM, fontSize: 12, fontWeight: '800', marginTop: 16 },
  laneHint: { marginTop: 6 },
  // Audience lines (AM-2): one line of counts under a lane title, and the
  // short notes that explain a hidden split.
  audLine: { color: '#ccc', fontSize: 12.5, lineHeight: 18, marginTop: 6 },
  audNote: { color: '#777', fontSize: 11.5, lineHeight: 17, marginTop: 6 },

  gamesRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 14 },
  gameChip: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: '#1c1c1c', borderRadius: 6, paddingVertical: 5, paddingHorizontal: 10,
  },
  gameChipText: { color: '#ccc', fontSize: 12, fontWeight: '600' },
  gameChipCount: { color: COLORS.GOLD, fontSize: 11, fontWeight: '800' },

  backButton: { marginTop: 22, alignSelf: 'flex-start' },
  backButtonText: { color: '#888', fontSize: 14 },
});
